import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import { useNavigate } from "react-router-dom";
import { Spin } from "antd";
import { EasyAvatar } from "fin-web-components";
import { resolveAppMediaUrl } from "../../utils/resolveAppMediaUrl";
import { avatarDisplayWord } from "../../utils/avatarDisplayWord";
import { getReadableTime } from "../../utils/getReadableTime";
import { useSafeAreaTopPad } from "../../utils/useSafeAreaTopPad";
import { useMainScrollPullRefresh } from "../../utils/useMainScrollPullRefresh";
import { h5NavigateTo } from "../../utils/miniProgramEnv";
import { SpaceMessageCenterApi } from "./SpaceMessageCenterApi";
import { SpaceMessageCenterPollModel } from "./SpaceMessageCenterPollModel";
import type { MessageConversationRow } from "./SpaceMessageCenterTypes";
import { SPACE_MESSAGES } from "./Config";
import "./SpaceMessagesStyle.css";

const CS_AVATAR = `${process.env.PUBLIC_URL || ""}/icons/tab-cs-active.svg`;

/** 分割线左起点：与会话名称左边缘对齐 = 左右内边距 + 头像宽 + 头像与文案间距 */
const DIVIDER_INSET_LEFT =
  SPACE_MESSAGES.rowPaddingX + SPACE_MESSAGES.avatarSize + SPACE_MESSAGES.rowGap;

function badgeText(count: number): string {
  if (count <= 0) return "";
  return count > 99 ? "99+" : String(count);
}

function pullRefreshHint(phase: string): string {
  if (phase === "refreshing") return "刷新中…";
  if (phase === "ready") return "松开刷新";
  return "下拉刷新";
}

function openChatPath(spaceId: string, row: MessageConversationRow): string {
  if (row.kind === "customer_service" || row.conversationKey === "cs") {
    return `/space/${spaceId}/messages/chat/cs`;
  }
  const pid = row.spaceProductId?.trim();
  if (pid && row.userSubscribed === false) {
    // 无权限：进产品页，返回时可回到消息列表
    return `/space/${spaceId}/subscribe/${encodeURIComponent(pid)}?from=messages`;
  }
  if (pid) {
    return `/space/${spaceId}/messages/chat/group/${encodeURIComponent(pid)}`;
  }
  return `/space/${spaceId}/messages`;
}

export const SpaceMessagesPage = observer(function SpaceMessagesPage(props: {
  spaceId: string;
  isLoggedIn: boolean;
  onLoginRequest: () => void;
  onUnreadTotalChange?: (count: number) => void;
}) {
  const { spaceId, isLoggedIn, onLoginRequest, onUnreadTotalChange } = props;
  const navigate = useNavigate();
  const pageRef = useRef<HTMLDivElement>(null);
  const topPad = useSafeAreaTopPad(pageRef);
  const pollModel = useMemo(() => new SpaceMessageCenterPollModel(), []);
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<MessageConversationRow[]>([]);

  const applyList = useCallback(
    (list: MessageConversationRow[], totalUnread: number) => {
      setRows(list);
      setLoading(false);
      onUnreadTotalChange?.(totalUnread);
    },
    [onUnreadTotalChange]
  );

  useEffect(() => {
    if (!isLoggedIn) {
      pollModel.reset();
      setRows([]);
      setLoading(false);
      onUnreadTotalChange?.(0);
      return;
    }
    pollModel.startPoll(spaceId, applyList);
    return () => {
      pollModel.stopPoll();
    };
  }, [applyList, isLoggedIn, onUnreadTotalChange, pollModel, spaceId]);

  const onPullRefresh = useCallback(async () => {
    await pollModel.refreshNow();
  }, [pollModel]);

  const { pullDistance, phase: pullPhase } = useMainScrollPullRefresh(
    onPullRefresh,
    isLoggedIn
  );
  const ptrVisible = pullDistance > 0 || pullPhase === "refreshing";

  const onRowClick = useCallback(
    (row: MessageConversationRow) => {
      if (!isLoggedIn) {
        onLoginRequest();
        return;
      }
      const subscribed = row.userSubscribed !== false;
      if (subscribed) {
        void SpaceMessageCenterApi.markRead(spaceId, row.conversationKey).then(() => {
          setRows((prev) =>
            prev.map((r) =>
              r.conversationKey === row.conversationKey ? { ...r, unreadCount: 0 } : r
            )
          );
          pollModel.syncOnce();
        });
      }
      h5NavigateTo(navigate, openChatPath(spaceId, row));
    },
    [isLoggedIn, navigate, onLoginRequest, pollModel, spaceId]
  );

  return (
    <div className="space-messages-page" ref={pageRef} style={{ paddingTop: topPad }}>
      <div className="space-messages-body">
        {isLoggedIn ? (
          <div
            className={
              ptrVisible
                ? "space-messages-ptr space-messages-ptr--visible"
                : "space-messages-ptr"
            }
            style={{ height: ptrVisible ? `${pullDistance}px` : 0 }}
            aria-hidden={!ptrVisible}
          >
            <span className="space-messages-ptr__text">
              {pullPhase === "refreshing" ? (
                <>
                  <Spin size="small" /> {pullRefreshHint(pullPhase)}
                </>
              ) : (
                pullRefreshHint(pullPhase)
              )}
            </span>
          </div>
        ) : null}
        {!isLoggedIn ? (
          <div className="space-messages-empty">
            <p>登录后查看消息</p>
            <button type="button" className="space-messages-empty__btn" onClick={onLoginRequest}>
              去登录
            </button>
          </div>
        ) : loading && rows.length === 0 && pullPhase !== "refreshing" ? (
          <div className="space-messages-loading">
            <Spin />
          </div>
        ) : rows.length === 0 ? (
          <div className="space-messages-empty">
            <p>暂无消息</p>
          </div>
        ) : (
          <ul
            className="space-messages-list"
            role="list"
            style={{ paddingTop: SPACE_MESSAGES.listPaddingTop }}
          >
            {rows.map((row) => {
              const unread = Math.max(0, row.unreadCount ?? 0);
              const badge = badgeText(unread);
              const avatarSrc =
                row.kind === "customer_service"
                  ? CS_AVATAR
                  : resolveAppMediaUrl(row.avatarUrl) ?? undefined;
              const word = avatarDisplayWord(row.displayName);
              return (
                <li key={row.conversationKey}>
                  <button
                    type="button"
                    className="space-messages-row"
                    style={{
                      gap: SPACE_MESSAGES.rowGap,
                      padding: `${SPACE_MESSAGES.rowSpacing}px ${SPACE_MESSAGES.rowPaddingX}px`,
                      ["--space-messages-divider-inset" as string]: `${DIVIDER_INSET_LEFT}px`,
                      ["--space-messages-divider-color" as string]: SPACE_MESSAGES.dividerColor,
                    }}
                    onClick={() => onRowClick(row)}
                  >
                    <span className="space-messages-row__avatar-wrap">
                      <EasyAvatar
                        url={avatarSrc ?? null}
                        word={word}
                        shape="square"
                        squareRadius={4}
                        size={SPACE_MESSAGES.avatarSize}
                      />
                      {badge ? (
                        <span
                          className={`space-messages-row__badge${
                            badge.length === 1 ? " space-messages-row__badge--compact" : ""
                          }`}
                          aria-label={`${unread} 条未读`}
                        >
                          {badge}
                        </span>
                      ) : null}
                    </span>
                    <span
                      className="space-messages-row__main"
                      style={{
                        paddingTop: SPACE_MESSAGES.nameOffsetFromAvatarTop,
                        gap: SPACE_MESSAGES.previewGapFromName,
                      }}
                    >
                      <span className="space-messages-row__top">
                        <span className="space-messages-row__name">{row.displayName}</span>
                        <span className="space-messages-row__time">
                          {row.lastMessageAt ? getReadableTime(row.lastMessageAt) : ""}
                        </span>
                      </span>
                      <span className="space-messages-row__preview">
                        {row.lastMessagePreview?.trim() || " "}
                      </span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
});

export default SpaceMessagesPage;
